import moment from "moment";
import React from "react";
import clsx from "clsx";
import { toString } from "lodash";
import { Card, CardContent, CardHeader, makeStyles, Typography } from "@material-ui/core";
import { locale } from "@bonliva-crm/locale";
import {
  ConsultantServicePreference,
  ConsultantServicePreferenceStatus
} from "@bonliva-crm/graphql";
import { implicitToLocal } from "@bonliva-crm/common";
import { DropdownMenu, Option } from "./dropdown-menu";

interface ServicePreferenceCardProps {
  data: ConsultantServicePreference;
  menuOptions?: Option[];
  onClick?: () => void;
}

export const ServicePreferenceCard: React.FC<ServicePreferenceCardProps> = ({
  menuOptions = [],
  data,
  onClick
}) => {
  const classes = useStyles();

  const createdBy = `${locale.createdBy} ${data.createdByName} ${formatSystemDate(
    data.createdOn
  )}`;

  const haveCustomers = data.customers.length > 0;
  const customers = data.customers.map((customer) => customer.Name).join(", ");
  const period = `${formatDate(data.period.start)} - ${formatDate(data.period.end)}`;
  const isNullified = data.status === ConsultantServicePreferenceStatus.Nullified;

  const nullifiedText = isNullified
    ? `${locale.nullifiedBy} ${data.nullifiedByName} ${formatSystemDate(
        data.nullifiedOn
      )}`
    : null;

  return (
    <Card
      raised
      onClick={onClick}
      className={clsx(classes.itemWrap, {
        [classes.nullified]: isNullified
      })}
    >
      <CardHeader
        className={classes.cardHeader}
        title={
          <div className={classes.headRow}>
            <div className={classes.titleWrap}>
              <Typography variant={"h4"}>{locale.Description}</Typography>
              <Typography>{data.description}</Typography>
            </div>
            {menuOptions.length ? <DropdownMenu options={menuOptions} /> : null}
          </div>
        }
      />

      <CardContent>
        <div className={classes.content}>
          <Typography className={classes.contentItem}>
            <span className={classes.contentItemTitle}>{locale.period}:</span> {period}
          </Typography>
          {haveCustomers && (
            <Typography className={classes.contentItem}>
              <span className={classes.contentItemTitle}>{locale.appliesTo}:</span>{" "}
              {customers}
            </Typography>
          )}
        </div>

        <Typography className={classes.createdBy}>{createdBy}</Typography>
        {nullifiedText && (
          <Typography className={classes.nullifiedBy}>{nullifiedText}</Typography>
        )}

        {data.reminders.map((reminder) => (
          <Typography variant={"caption"} key={toString(reminder.createdAt)}>
            {`Påmind: ${moment(reminder.createdAt).format("L HH:mm")}`}
          </Typography>
        ))}
      </CardContent>
    </Card>
  );
};

function formatDate(date: string | Date) {
  return moment(implicitToLocal(date)).format("YYYY-MM-DD");
}

function formatSystemDate(date: string | Date) {
  return moment(date).format("YYYY-MM-DD");
}

const useStyles = makeStyles((theme) => ({
  titleWrap: {
    display: "flex",
    flexDirection: "column"
  },
  headRow: {
    display: "flex",
    alignItems: "flex-start"
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },
  contentItem: {
    fontSize: 16
  },
  contentItemTitle: {
    fontWeight: 500
  },
  createdBy: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.grey[300]}`,
    maxWidth: "max-content",
    fontSize: 14
  },
  nullifiedBy: {
    maxWidth: "max-content",
    fontSize: 14
  },
  itemWrap: {
    cursor: "pointer",
    padding: `0 ${theme.spacing(2)}px`
  },
  cardHeader: {
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px 0`
  },
  nullified: {
    background: "#ffdad7ad"
  }
}));
